import { useCallback, useEffect, useRef, useState } from "react";
import type { Locale } from "../templates/types";
import { getVoiceCapabilities } from "./capabilities";

// The Web Speech API's SpeechRecognition interface isn't part of
// TypeScript's lib.dom.d.ts (it's still non-standard/vendor-prefixed), so
// there's no ambient type to import. These are narrow local shapes covering
// only what this hook touches — deliberately loose (`any` for the
// constructor and result list) rather than a global `declare global`
// augmentation, so this file is the only place that has to know about it.
type SpeechRecognitionResultLike = {
  isFinal: boolean;
  0: { transcript: string };
};

type SpeechRecognitionEventLike = {
  resultIndex: number;
  results: ArrayLike<SpeechRecognitionResultLike>;
};

type SpeechRecognitionLike = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
};

// §16: consistent with useSpeech's locale→BCP-47 mapping.
const LANG_BY_LOCALE: Record<Locale, string> = { en: "en-US", es: "es-MX" };

function getRecognitionCtor(): (new () => SpeechRecognitionLike) | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLike;
    webkitSpeechRecognition?: new () => SpeechRecognitionLike;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/**
 * Input side of §16. Interim (non-final) results are exposed as plain state
 * for the caller to render greyed-out — this hook never touches the DOM.
 *
 * The final transcript is handed back ONLY via the `onFinalResult` callback
 * passed to `start()`. This hook does not send anything anywhere and does
 * not know what "send" means — the caller (Composer) is responsible for
 * putting the text in the input box for review, never for auto-submitting
 * it. An ASR error must not silently become verbatim research data (§16).
 */
export function useDictation() {
  const [listening, setListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const canListen = getVoiceCapabilities().canListen;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
    recognitionRef.current = null;
    setListening(false);
    setInterimTranscript("");
  }, []);

  useEffect(() => {
    // Stop cleanly on unmount rather than leaving the mic hot behind a
    // component that no longer exists.
    return () => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
    };
  }, []);

  const start = useCallback((locale: Locale, opts: { onFinalResult: (text: string) => void }) => {
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;

    recognitionRef.current?.stop();

    const recognition = new Ctor();
    recognition.lang = LANG_BY_LOCALE[locale] ?? "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;

    recognition.onresult = (event) => {
      let interim = "";
      let final = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0]?.transcript ?? "";
        if (result.isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (interim) setInterimTranscript(interim);
      if (final) {
        setInterimTranscript("");
        opts.onFinalResult(final);
      }
    };
    recognition.onend = () => {
      setListening(false);
    };
    recognition.onerror = () => {
      // Fail closed to "not listening" rather than leaving the UI stuck in
      // a spinning state with no way out.
      setListening(false);
      setInterimTranscript("");
    };

    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  }, []);

  return { listening, interimTranscript, start, stop, canListen };
}
